import React, { useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { CONTACT, LINKS } from '../data/site';
type Faq = {
  question: string;
  answer: React.ReactNode;
};
const FAQS: Faq[] = [{
  question: 'Can you help me choose metal shaping tools?',
  answer: <p data-id="element-74" data-mp="1gjlubn3sb8dp">
        Yes. Tell us the projects you have in mind, the material you are working in and what equipment you
        already own, and we will point you to the tools that make sense for the work.{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.contact} data-id="element-75" data-mp="28ajicdv7i4su">
          Contact the shop
        </a>{' '}
        for selection help, or browse{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.shopAll} data-id="element-76" data-mp="1zhy1x4f1sa76">
          the full catalog
        </a>
        .
      </p>
}, {
  question: 'How do I arrange a workshop?',
  answer: <p data-id="element-77" data-mp="2s03d4keglchv">
        Workshops run on demand for small groups of one to five people. Get in touch to discuss your
        interests, experience and scheduling; tools and practice materials are provided.{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.workshops} data-id="element-78" data-mp="10rm1568ptlcw">
          See workshop details
        </a>{' '}
        or ask about{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.privateTraining} data-id="element-79" data-mp="19t2e9lqx01kt">
          private training
        </a>
        .
      </p>
}, {
  question: 'What should I send for a fabrication estimate?',
  answer: <p data-id="element-80" data-mp="1xhvc5nr3kji2">
        Include the material, thickness, dimensions, quantity and your timeline, plus any drawings or photos
        of the part or vehicle. Email them to{' '}
        <a className="break-words underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href="mailto:jeff@fournierenterprises.com" data-id="element-81" data-mp="36q0bw5ux0rn">
          jeff@fournierenterprises.com
        </a>
        , or read the{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.faq} data-id="element-82" data-mp="1yb7lwzrcvzb5">
          full FAQ
        </a>{' '}
        for more detail.
      </p>
}, {
  question: 'Do you ship internationally?',
  answer: <p data-id="element-83" data-mp="2c9xgo8qpdmwc">
        Email{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={`mailto:${CONTACT.email}`} data-id="element-84" data-mp="2ca7ay8is6g7u">
          {CONTACT.email}
        </a>{' '}
        with the items you want and your complete delivery address, and we will quote the shipping for your
        order.
      </p>
}];
export function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="border-t border-steel-200 bg-cream" aria-labelledby="faq-heading" data-id="element-85" data-mp="2zyyte86kjk7f">
      <div className="mx-auto grid max-w-[1240px] gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16 lg:py-20" data-id="element-86" data-mp="34x0dmwfqdpkh">
        <div data-id="element-87" data-mp="tbrgp16d6qxt">
          <p className="font-display text-[13px] uppercase tracking-eyebrow text-fred" data-id="element-88" data-mp="t83llxl9twgz">Questions</p>
          <h2 id="faq-heading" className="mt-4 font-display text-[36px] font-bold uppercase leading-[0.95] text-charcoal sm:text-[46px]" data-id="element-89" data-mp="od7tkxg1b93v">
            Metalworking questions,
            <br data-id="element-90" data-mp="fcrue06bqo4t" />
            answered.
          </h2>
          <p className="mt-5 max-w-[38ch] text-[16px] leading-relaxed text-steel-600" data-id="element-91" data-mp="1bzmqyb2rpmm6">
            If yours isn’t here, call{' '}
            <a href={CONTACT.phoneHref} className="font-medium text-charcoal underline decoration-steel-200 underline-offset-4 hover:decoration-fred" data-id="element-92" data-mp="18176adu5sqbx">
              {CONTACT.phoneLabel}
            </a>{' '}
            or read the{' '}
            <a href={LINKS.faq} className="text-charcoal underline decoration-steel-200 underline-offset-4 hover:decoration-fred" data-id="element-93" data-mp="1vpgjwsh7y7rz">
              full FAQ
            </a>
            .
          </p>
        </div>

        <div className="border-t border-steel-200" data-id="element-94" data-mp="1fhagws7isslq">
          {FAQS.map((faq, index) => {
          const isOpen = openIndex === index;
          const panelId = `faq-panel-${index}`;
          const buttonId = `faq-button-${index}`;
          return <div key={faq.question} className="border-b border-steel-200" data-id="element-95" data-mp="16h45rczao4pq" data-mpi={index}>
                <h3 data-id="element-96" data-mp="1u7q0k2kafshs">
                  <button type="button" id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenIndex(isOpen ? null : index)} className="flex min-h-[64px] w-full items-center justify-between gap-6 py-4 text-left font-display text-[22px] uppercase leading-tight text-charcoal transition-colors duration-150 ease-out hover:text-fred sm:text-[24px]" data-id="element-97" data-mp="1u786ncl3lqd1">
                    {faq.question}
                    {isOpen ? <MinusIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-fred" data-id="element-98" data-mp="2r99qkzw9ml9k" /> : <PlusIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-steel-400" data-id="element-99" data-mp="2n751megrict9" />}
                  </button>
                </h3>
                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen} className="max-w-[60ch] pb-6 text-[15px] leading-relaxed text-steel-600" data-id="element-100" data-mp="2w2kscmm80bum">
                  {faq.answer}
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}